import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import type { FeatureFlagUserOption } from '../../../types/features/featureFlags';
import featureFlagUserOptionsService from '../services/featureFlagUserOptionsService';
import {
  mapAdminStoreUserToFeatureFlagUserOption,
} from '../mappers/featureFlagUserOptionMapper';

const FEATURE_FLAG_USER_OPTIONS_PAGE_SIZE = 100;

export const useFeatureFlagUserOptions = (enabled: boolean) => {
  const requestIdRef = useRef(0);
  const [error, setError] = useState('');
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [users, setUsers] = useState<FeatureFlagUserOption[]>([]);
  const debouncedSearch = useDebouncedValue(search, 300);

  const fetchUsers = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');

    try {
      const response = await featureFlagUserOptionsService.getFeatureFlagUsers({
        page,
        pageSize: FEATURE_FLAG_USER_OPTIONS_PAGE_SIZE,
        search: debouncedSearch,
      });

      if (requestId === requestIdRef.current) {
        const storeUsers = Array.isArray(response.users) ? response.users : [];
        setHasNextPage(Boolean(response.has_next));
        setHasPreviousPage(Boolean(response.has_previous));
        setPage(response.page || page);
        setTotalPages(response.total_pages || 1);
        setTotalUsers(response.total_users || storeUsers.length);
        setUsers(storeUsers.map(mapAdminStoreUserToFeatureFlagUserOption));
      }
    } catch (requestError: any) {
      if (requestId === requestIdRef.current) {
        setError(requestError?.message || 'Failed to load users');
        setHasNextPage(false);
        setHasPreviousPage(false);
        setTotalPages(1);
        setTotalUsers(0);
        setUsers([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    if (enabled) {
      fetchUsers();
    }
  }, [enabled, fetchUsers]);

  const handleSearchChange = useCallback((value: string) => {
    setPage(1);
    setSearch(value);
  }, []);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(Math.max(1, nextPage));
  }, []);

  const remainingUsers = Math.max(totalUsers - (page * FEATURE_FLAG_USER_OPTIONS_PAGE_SIZE), 0);

  return {
    error,
    handlePageChange,
    handleSearchChange,
    hasNextPage,
    hasPreviousPage,
    loading,
    page,
    remainingUsers,
    search,
    totalPages,
    totalUsers,
    users,
  };
};
